import React, { useEffect, useRef, useState } from "react";
import classes from "./EditServices.module.scss";
import { Container, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { DDD, E2Data } from "../lib/ddd/ddd";
import ExportOrganizationData from "../components/servicesComponents/administration/ExportOrganizationData";
import { useTranslation } from "react-i18next";
import PendingIcon from '@mui/icons-material/Pending';
import Button from "../components/UI/Button";
import ErrorIcon from '@mui/icons-material/Error';
import { 
    Box, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    TextField, 
    Tabs, 
    Tab, 
    Typography, 
    List, 
    ListItem, 
    Link,
    ClickAwayListener,
    Popper,
    Paper,
    InputAdornment,
    IconButton,
    Slide,
    Collapse,
    Skeleton,
    Table, TableHead, TableRow, TableCell, TableBody,
    Tooltip as MuiTooltip,
    Checkbox,
    Menu
} from '@mui/material';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import OutboxIcon from '@mui/icons-material/Outbox';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

const OrganizationSettings = ({invoiceType, modal}) => {
    const { t } = useTranslation();
    const [type, setType] = useState("null"); // Invoice Type
    const [expired, setExpired] = useState("null");
    const [status, setStatus] = useState("null");
    const [filter, setFilter] = useState("");
    const [mode, setMode] = useState("view");
    const form = useRef();

    const handleTypeChange = (event) => {
        setType(event.target.value||"null");
    };

    const handleExpiredChange = (event) => {
        setExpired(event.target.value||"null");
    };

    const handleStatusChange = (event) => {
        setStatus(event.target.value||"null");
    };

    useEffect(() => {
        let _initFilter = "";
        if(type !== "null")
            _initFilter += type === "iei" ? "Destination = 'IEI' AND" : "Destination = 'REI' AND";
        if(expired !== "null")
            _initFilter += expired === "expired" ? " XLSExpirationDate < Now() AND" : " XLSExpirationDate >= Now() AND";
        if(status !== "null")
            _initFilter += status === "finished" ? " Status = 'Finished' AND" : status === "inprogress" ? " Status = 'In progress' AND" : " Status = 'Error' AND";

        _initFilter = _initFilter.trim();
        if (_initFilter.endsWith(" AND"))
            _initFilter = _initFilter.slice(0, -4).trim();
        
        if(_initFilter !== filter) 
            setFilter(_initFilter);
        
    }, [type, expired, status]);

    const onSubmitClick = () => {
        if(!form?.current) return;
        form.current.submit();
    }

    const onSubmit = (data) => {
        setMode("view");
    }

    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    const isoDate = threeMonthsAgo.toISOString().split('T')[0]; // YYYY-MM-DD

    return (
    <>
      <Helmet>
        <title>Organization settings</title>
      </Helmet>
      <div className={`h-100 w-100 p-2 d-flex flex-column ${classes.container}`} style={modal ? {maxHeight:"85vh"} : {}}>
        {/* <h3>{t("Invoices export")}</h3> */}
        <Box display="flex" flexWrap="wrap" gap={2} p={2}>
            <h4 style={{paddingTop:"7px"}}>{t("Invoices export")}
                <OverlayTrigger placement="top" overlay={<Tooltip>{mode === "view" ? t(`Values with a create date older than 3 months are not displayed (${isoDate})`) : t("Only invoices in Production mode will be collated. (Invoices where Step < 35 or mode is unassgined are not included)")}</Tooltip>}>
                    <HelpOutlineIcon sx={{ color: 'var(--main-blue)', fontSize: 20, cursor: 'default', marginLeft:"0.25rem" }} />
                </OverlayTrigger>
            </h4>
            {mode === "view" && <><FormControl variant="outlined" size="small"
                sx={{
                    minWidth: 140,
                    '& .MuiOutlinedInput-root': {
                    borderRadius: '1rem',
                    padding: '0.5rem',
                    height: '2.5rem',
                    '& fieldset': {
                        borderColor: 'var(--main-blue)',
                    },
                    '&:hover fieldset': {
                        borderColor: 'var(--main-blue)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'var(--main-blue)',
                    }
                    }
                }}>
                    <InputLabel>{t("Invoice Direction")}</InputLabel>
                    <Select value={type ?? 'null'} onChange={handleTypeChange} label={t("Invoice Direction")}>
                        <MenuItem value="null">{t("All")}</MenuItem>
                        <MenuItem value="iei">
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                }}>
                                <OutboxIcon className="main-blue"/>
                                {t("Issued")}
                            </Box>
                        </MenuItem>
                        <MenuItem value="rei">
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                }}>
                                <MoveToInboxIcon className="main-blue"/>
                                {t("Received")}
                            </Box>
                        </MenuItem>
                    </Select>
            </FormControl>
            <FormControl variant="outlined" size="small"
                sx={{
                    minWidth: 140,
                    '& .MuiOutlinedInput-root': {
                    borderRadius: '1rem',
                    padding: '0.5rem',
                    height: '2.5rem',
                    '& fieldset': {
                        borderColor: 'var(--main-blue)',
                    },
                    '&:hover fieldset': {
                        borderColor: 'var(--main-blue)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'var(--main-blue)',
                    }
                    }
                }}>
                    <InputLabel>{t("Expiration")}</InputLabel>
                    <Select value={expired ?? 'null'} onChange={handleExpiredChange} label={t("Expiration")}>
                        <MenuItem value="null">{t("All")}</MenuItem>
                        <MenuItem value="unexpired">
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                }}>
                                <CheckCircleIcon  className="main-blue"/>
                                {t("Not expired")}
                            </Box>
                        </MenuItem>
                        <MenuItem value="expired">
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                }}>
                                <HighlightOffIcon className="main-blue"/>
                                {t("Expired")}
                            </Box>
                        </MenuItem>
                    </Select>
            </FormControl>
            <FormControl variant="outlined" size="small"
                sx={{
                    minWidth: 140,
                    '& .MuiOutlinedInput-root': {
                    borderRadius: '1rem',
                    padding: '0.5rem',
                    height: '2.5rem',
                    '& fieldset': {
                        borderColor: 'var(--main-blue)',
                    },
                    '&:hover fieldset': {
                        borderColor: 'var(--main-blue)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'var(--main-blue)',
                    }
                    }
                }}>
                    <InputLabel>{t("Status")}</InputLabel>
                    <Select value={status ?? 'null'} onChange={handleStatusChange} label={t("Status")}>
                        <MenuItem value="null">{t("All")}</MenuItem>
                        <MenuItem value="finished">
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                }}>
                                <CheckCircleIcon style={{color:"green"}}/>
                                {t("Finished")}
                            </Box>
                        </MenuItem>
                        <MenuItem value="inprogress">
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                }}>
                                
                                <PendingIcon style={{color:"var(--main-blue)"}}/>
                                {t("In progress")}
                            </Box>
                        </MenuItem>
                        <MenuItem value="error">
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                }}>
                                <ErrorIcon style={{color:"red"}}/>
                                {t("Error")}
                            </Box>
                        </MenuItem>
                    </Select>
            </FormControl></>}
        </Box>
        <div className={`${classes.panel} flex-grow-1 overflow-auto`}>
            <ExportOrganizationData type={type} expired={expired} status={status} filter={filter} modal={modal} mode={mode} ref={form} onSubmit={onSubmit}/>
        </div>
        {mode === "view" && <div className="text-end mt-3 p-4">
            <Button className="ps-2 pe-2" onClick={() => setMode("create")}>{t("Create new export")}</Button>
        </div>}
        {mode==="create" && <div className="text-end mt-3 p-4">
            <Button className="ps-2 pe-2" onClick={() => setMode("view")}>{t("Cancel")}</Button>
            <Button className="ms-2 ps-2 pe-2" onClick={onSubmitClick}>{t("Create new export")}</Button>
        </div>}
      </div>
    </>
  );
};

export default OrganizationSettings;
